// src/components/sku/ProductVariantSelectModal.tsx
import * as React from "react";
import { Modal, Table, Input, Typography, Empty, InputNumber, Checkbox } from "antd";
import type { ColumnsType } from "antd/es/table";
import CustomButton from "src/components/common/CustomButton";
import { getProductListApi } from "src/api/product/productApi";
import type { ProductListItem, Variant } from "src/types/product";
import { snackbarStore } from "src/store/snackbarStore";
import { createSkuMappingApi } from "src/api/sku/skuApi";

const toast = (message: string, severity: "success" | "error" | "info" | "warning" = "info") =>
  snackbarStore.getState().addSnackbar({ message, severity });

type ProductWithVariants = ProductListItem & {
  variants?: Variant[];
};

type SelectedProduct = {
  productId: number;
  variantId?: number | null;
  productCode: string;
  productName: string;
  variantName?: string;
  quantityPerOrder: number;
};

type Props = {
  open: boolean;
  skuId?: number; // optional for create mode
  onClose: () => void;
  onSuccess?: () => void;
  onSelectProducts?: (products: SelectedProduct[]) => void; // for create mode
};

export default function ProductVariantSelectModal({
  open,
  skuId,
  onClose,
  onSuccess,
  onSelectProducts,
}: Props) {
  const [list, setList] = React.useState<ProductWithVariants[]>([]);
  const [loading, setLoading] = React.useState(false);
  const [page, setPage] = React.useState(0);
  const [size] = React.useState(10);
  const [totalElements, setTotalElements] = React.useState(0);
  const [searchValue, setSearchValue] = React.useState("");
  const [selectedProducts, setSelectedProducts] = React.useState<SelectedProduct[]>([]);
  const [submitting, setSubmitting] = React.useState(false);

  React.useEffect(() => {
    if (open) {
      setPage(0);
      setSearchValue("");
      setSelectedProducts([]);
    }
  }, [open]);

  React.useEffect(() => {
    if (!open) return;
    fetchList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, page, searchValue]);

  const fetchList = async () => {
    setLoading(true);
    try {
      const res = await getProductListApi({
        productName: searchValue || undefined,
        page,
        size,
        order: "createdAt,desc",
        include: "variants,addonMappings,skuMappings,volumePrices",
      });
      if (res.statusCode === 1 && res.data) {
        setList(res.data.content);
        setTotalElements(res.data.page.totalElements);
      }
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    setPage(0);
  };

  // 부모 상품 선택 (옵션 상품인 경우 모든 variant도 선택)
  const handleSelectProduct = (product: ProductWithVariants) => {
    const productId = Number(product.productId);
    const hasVariants =
      product.productType === "OPTION" && product.variants && product.variants.length > 0;

    // 이미 선택되어 있는지 확인 (부모 또는 자식 중 하나라도)
    const hasAnySelected = selectedProducts.some((p) => p.productId === productId);

    if (hasAnySelected) {
      // 선택 해제: 해당 상품의 모든 항목 제거
      setSelectedProducts((prev) => prev.filter((p) => p.productId !== productId));
    } else {
      // 선택: 부모 또는 모든 variants 추가
      if (hasVariants) {
        // 옵션 상품: 모든 variants 추가
        const variantSelections: SelectedProduct[] = product.variants!.map((variant) => ({
          productId: productId,
          variantId: variant.variantId || null,
          productCode: product.productCode || "",
          productName: product.productName,
          variantName: variant.variantName,
          quantityPerOrder: 1,
        }));
        setSelectedProducts((prev) => [...prev, ...variantSelections]);
      } else {
        // 단일 상품: 부모만 추가
        setSelectedProducts((prev) => [
          ...prev,
          {
            productId: productId,
            variantId: null,
            productCode: product.productCode || "",
            productName: product.productName,
            quantityPerOrder: 1,
          },
        ]);
      }
    }
  };

  // 개별 variant 선택
  const handleSelectVariant = (product: ProductWithVariants, variant: Variant) => {
    const productId = Number(product.productId);
    const variantId = variant.variantId || null;

    const existing = selectedProducts.find(
      (p) => p.productId === productId && p.variantId === variantId
    );

    if (existing) {
      // 선택 해제
      setSelectedProducts((prev) =>
        prev.filter((p) => !(p.productId === productId && p.variantId === variantId))
      );
    } else {
      // 선택
      setSelectedProducts((prev) => [
        ...prev,
        {
          productId: productId,
          variantId: variantId,
          productCode: product.productCode || "",
          productName: product.productName,
          variantName: variant.variantName,
          quantityPerOrder: 1,
        },
      ]);
    }
  };

  const handleQuantityChange = (
    productId: number,
    variantId: number | null | undefined,
    quantity: number
  ) => {
    setSelectedProducts((prev) =>
      prev.map((p) =>
        p.productId === productId && p.variantId === variantId
          ? { ...p, quantityPerOrder: quantity }
          : p
      )
    );
  };

  const handleConfirm = async () => {
    if (selectedProducts.length === 0) {
      toast("선택된 상품이 없습니다.", "warning");
      return;
    }

    // Create mode: just pass selected products to parent
    if (!skuId && onSelectProducts) {
      onSelectProducts(selectedProducts);
      onClose();
      return;
    }

    // Edit mode: create mappings
    if (!skuId) {
      toast("SKU ID가 없습니다.", "error");
      return;
    }

    setSubmitting(true);

    try {
      for (const product of selectedProducts) {
        await createSkuMappingApi({
          skuId,
          productId: product.productId,
          variantId: product.variantId ?? null,
          quantityPerOrder: product.quantityPerOrder,
        });
      }

      toast(`${selectedProducts.length}개의 연결이 생성되었습니다.`, "success");
      if (onSuccess) onSuccess();
      onClose();
    } catch (e: any) {
      toast(e?.message || "연결 생성 중 오류가 발생했습니다.", "error");
    } finally {
      setSubmitting(false);
    }
  };

  // 확장 가능한 하위 테이블 (variants)
  const expandedRowRender = (product: ProductWithVariants) => {
    if (!product.variants || product.variants.length === 0) return null;

    const variantColumns: ColumnsType<Variant> = [
      {
        title: "선택",
        width: 60,
        align: "center",
        render: (_, variant) => {
          const productId = Number(product.productId);
          const variantId = variant.variantId || null;
          const isSelected = selectedProducts.some(
            (p) => p.productId === productId && p.variantId === variantId
          );
          return (
            <Checkbox
              checked={isSelected}
              onChange={(e) => {
                e.stopPropagation();
                handleSelectVariant(product, variant);
              }}
              onClick={(e) => e.stopPropagation()}
            />
          );
        },
      },
      {
        title: "옵션명",
        dataIndex: "variantName",
        key: "variantName",
        width: 200,
        align: "left",
      },
      {
        title: "옵션가",
        dataIndex: "optionPrice",
        key: "optionPrice",
        width: 120,
        align: "right",
        render: (price) => `+${price.toLocaleString()}원`,
      },
      {
        title: "판매상태",
        width: 100,
        align: "center",
        render: (_, variant) => (variant.isSoldout ? "품절" : "판매중"),
      },
      {
        title: "주문당 출고수량",
        width: 150,
        align: "center",
        render: (_, variant) => {
          const productId = Number(product.productId);
          const variantId = variant.variantId || null;
          const selected = selectedProducts.find(
            (p) => p.productId === productId && p.variantId === variantId
          );
          return selected ? (
            <InputNumber
              min={1}
              value={selected.quantityPerOrder}
              onChange={(value) => handleQuantityChange(productId, variantId, value || 1)}
              size="small"
              style={{ width: 80 }}
            />
          ) : (
            "-"
          );
        },
      },
    ];

    return (
      <Table
        columns={variantColumns}
        dataSource={product.variants}
        pagination={false}
        rowKey={(variant) => `${product.productId}-${variant.variantId || variant.variantCode}`}
        size="small"
        scroll={{ x: 1000 }}
      />
    );
  };

  const columns: ColumnsType<ProductWithVariants> = [
    {
      title: "선택",
      width: 60,
      align: "center",
      render: (_, product) => {
        const productId = Number(product.productId);
        const hasVariants =
          product.productType === "OPTION" && product.variants && product.variants.length > 0;

        // 옵션 상품인 경우: 모든 variants가 선택되어 있는지 확인
        let isSelected = false;
        if (hasVariants) {
          const variantCount = product.variants!.length;
          const selectedCount = selectedProducts.filter((p) => p.productId === productId).length;
          isSelected = selectedCount === variantCount && variantCount > 0;
        } else {
          // 단일 상품: 해당 상품이 선택되어 있는지 확인
          isSelected = selectedProducts.some((p) => p.productId === productId);
        }

        return (
          <Checkbox
            checked={isSelected}
            indeterminate={
              hasVariants && selectedProducts.some((p) => p.productId === productId) && !isSelected
            }
            onChange={(e) => {
              e.stopPropagation();
              handleSelectProduct(product);
            }}
            onClick={(e) => e.stopPropagation()}
          />
        );
      },
    },
    {
      title: "상품 ID",
      dataIndex: "productId",
      key: "productId",
      width: 100,
      align: "right",
    },
    {
      title: "상품코드",
      dataIndex: "productCode",
      key: "productCode",
      width: 150,
      align: "left",
    },
    {
      title: "상품명",
      dataIndex: "productName",
      key: "productName",
      width: 250,
      align: "left",
    },
    {
      title: "상품유형",
      dataIndex: "productType",
      key: "productType",
      width: 100,
      align: "center",
      render: (type) => (type === "OPTION" ? "옵션상품" : "단일상품"),
    },
    {
      title: "판매상태",
      dataIndex: "saleStatus",
      key: "saleStatus",
      width: 120,
      align: "center",
    },
    {
      title: "주문당 출고수량",
      width: 150,
      align: "center",
      render: (_, product) => {
        const productId = Number(product.productId);
        const hasVariants =
          product.productType === "OPTION" && product.variants && product.variants.length > 0;

        // 옵션 상품인 경우 "-" 표시 (하위에서 설정)
        if (hasVariants) {
          return "-";
        }

        // 단일 상품
        const selected = selectedProducts.find((p) => p.productId === productId);
        return selected ? (
          <InputNumber
            min={1}
            value={selected.quantityPerOrder}
            onChange={(value) => handleQuantityChange(productId, null, value || 1)}
            size="small"
            style={{ width: 80 }}
          />
        ) : (
          "-"
        );
      },
    },
  ];

  const expandableConfig = {
    expandedRowRender,
    rowExpandable: (record: ProductWithVariants) =>
      record.productType === "OPTION" && !!record.variants && record.variants.length > 0,
  };

  return (
    <Modal
      open={open}
      onCancel={onClose}
      width={1200}
      centered
      title={<Typography.Text strong>상품 선택</Typography.Text>}
      footer={[
        <CustomButton key="cancel" variant="outlined" onClick={onClose} disabled={submitting}>
          취소
        </CustomButton>,
        <CustomButton
          key="confirm"
          variant="contained"
          onClick={handleConfirm}
          disabled={submitting || selectedProducts.length === 0}
        >
          확인 ({selectedProducts.length}개 선택)
        </CustomButton>,
      ]}
    >
      <div style={{ marginBottom: 16 }}>
        <Input.Search
          placeholder="상품명 검색"
          value={searchValue}
          onChange={(e) => setSearchValue(e.target.value)}
          onSearch={handleSearch}
          style={{ width: 300 }}
        />
      </div>

      <Table
        columns={columns}
        dataSource={list}
        loading={loading}
        rowKey="productId"
        expandable={expandableConfig}
        pagination={{
          current: page + 1,
          pageSize: size,
          total: totalElements,
          onChange: (p) => setPage(p - 1),
          showSizeChanger: false,
        }}
        scroll={{ x: 1000 }}
        locale={{ emptyText: <Empty description="상품이 없습니다." /> }}
      />
    </Modal>
  );
}
